import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import RulebookPickerTab from "./RulebookPickerTab";
import { filterRulebooksBySearch, RulebookSelectionSort, sortRulebooks } from "./rulebookSelection";
const SEARCH_DEBOUNCE_MS = 300;
/** The picker's own state: tab, search, sort and the pick (with its summary), reset whenever it opens. */
export default function useRulebookPickerState({ isOpen, rulebooks, selectedRulebookId, }) {
    const [stateTab, setStateTab] = useState(RulebookPickerTab.Mine);
    const [stateSearch, setStateSearch] = useState("");
    const [stateSort, setStateSort] = useState(RulebookSelectionSort.Latest);
    const [statePick, setStatePick] = useState({ id: selectedRulebookId });
    const [debouncedSearch] = useDebounce(stateSearch, SEARCH_DEBOUNCE_MS);
    // Reset on the open transition only — `selectedRulebookId` changes while open (e.g. the form's pick changed)
    // must not wipe the tab, search, sort or pick the user is in the middle of.
    const refWasOpen = useRef(false);
    const refSelectedId = useRef(selectedRulebookId);
    refSelectedId.current = selectedRulebookId;
    // Whether the pick came from a card click; while false, the pick follows `selectedRulebookId` even mid-session
    // (the attachment lookup's Retry can resolve a new selection after the picker opened).
    const refUserPicked = useRef(false);
    useEffect(() => {
        const wasOpen = refWasOpen.current;
        refWasOpen.current = isOpen;
        if (isOpen && !wasOpen) {
            refUserPicked.current = false;
            setStatePick({ id: refSelectedId.current });
            setStateTab(RulebookPickerTab.Mine);
            setStateSearch("");
            setStateSort(RulebookSelectionSort.Latest);
        }
    }, [isOpen]);
    useEffect(() => {
        if (isOpen && !refUserPicked.current) {
            setStatePick({ id: selectedRulebookId });
        }
    }, [isOpen, selectedRulebookId]);
    const visibleRulebooks = useMemo(() => filterRulebooksBySearch({
        rulebooks: sortRulebooks({ rulebooks: rulebooks ?? [], sort: stateSort }),
        query: debouncedSearch,
        keepId: statePick.id,
    }), [rulebooks, stateSort, debouncedSearch, statePick.id]);
    const handleSearch = useCallback((value) => setStateSearch(value ?? ""), []);
    const pickMine = useCallback((rulebookId) => {
        refUserPicked.current = true;
        setStatePick({ id: rulebookId, rulebook: rulebooks?.find((rulebook) => rulebook.id === rulebookId) });
    }, [rulebooks]);
    const pickDiscovered = useCallback((rulebook) => {
        refUserPicked.current = true;
        setStatePick({ id: rulebook.id, rulebook });
    }, []);
    // A pick from one tab may not be listed in the other, which would leave the confirm button acting on a hidden card.
    const handleTab = useCallback((tab) => {
        setStateTab(tab);
        refUserPicked.current = false;
        setStatePick({ id: selectedRulebookId });
    }, [selectedRulebookId]);
    const isUnchanged = statePick.id === selectedRulebookId;
    return {
        stateTab,
        stateSearch,
        stateSort,
        setStateSort,
        statePick,
        visibleRulebooks,
        handleSearch,
        pickMine,
        pickDiscovered,
        handleTab,
        isUnchanged,
    };
}
